import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  deleteFanslyFypItem,
  fetchFanslyFypPreview,
  getApiErrorMessage,
  getFanslyFyp,
  getFanslyFypAnalytics,
  publishFanslyFypItemNow,
  retryFanslyFypItem,
  updateFanslyFypItem,
  updateFanslyFypSettings,
  uploadFanslyFypArchive,
  uploadFanslyFypMedia,
} from '../api'
import '../styles/components/fansly-fyp.css'

const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
]

const DEFAULT_SETTINGS = {
  enabled: false,
  timezone: 'UTC',
  start_at: '',
  interval_minutes: '360',
  jitter_minutes: '0',
  daily_start_time: '09:00',
  daily_end_time: '23:00',
  weekdays: WEEKDAYS.map((day) => day.value),
  max_posts_per_day: '4',
  post_ttl_days: '0',
  default_caption: '',
  wall_ids: '',
  fyp_enabled: true,
}

const STATUS_LABELS = {
  queued: 'Queued',
  scheduled: 'Scheduled',
  uploading: 'Uploading',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  review: 'Review',
}

const MAX_MEDIA_UPLOAD_BYTES = 240 * 1024 * 1024

function unwrapData(value) {
  return value?.data ?? value ?? {}
}

function itemList(value) {
  const data = unwrapData(value)
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  if (Array.isArray(data.list)) return data.list
  if (Array.isArray(data.queue)) return data.queue
  return []
}

function campaignData(value) {
  const data = unwrapData(value)
  return data.settings ?? data.campaign ?? data
}

function minuteToTime(value, fallback) {
  const minute = Number(value)
  if (!Number.isFinite(minute)) return fallback
  const hours = String(Math.floor(minute / 60)).padStart(2, '0')
  const minutes = String(minute % 60).padStart(2, '0')
  return `${hours}:${minutes}`
}

function dateParts(value, timezone) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date)
    return Object.fromEntries(parts.map((part) => [part.type, part.value]))
  } catch {
    return null
  }
}

function toDateTimeInput(value, timezone) {
  if (!value) return ''
  const parts = dateParts(value, timezone)
  if (parts) return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/)
  return match?.[1] ?? ''
}

function zonedDateTimeToIso(value, timezone) {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!match) return value
  const desired = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  )
  let estimate = desired
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const rendered = dateParts(estimate, timezone)
    if (!rendered) return new Date(desired).toISOString()
    const renderedUtc = Date.UTC(
      Number(rendered.year),
      Number(rendered.month) - 1,
      Number(rendered.day),
      Number(rendered.hour),
      Number(rendered.minute),
      Number(rendered.second),
    )
    estimate += desired - renderedUtc
  }
  return new Date(estimate).toISOString()
}

function fromCampaign(value) {
  const campaign = campaignData(value)
  const weekdays = Array.isArray(campaign.weekdays) && campaign.weekdays.length
    ? campaign.weekdays.map(Number).filter((day) => day >= 0 && day <= 6)
    : DEFAULT_SETTINGS.weekdays
  return {
    enabled: Boolean(campaign.enabled),
    timezone: campaign.timezone || DEFAULT_SETTINGS.timezone,
    start_at: toDateTimeInput(campaign.start_at, campaign.timezone),
    interval_minutes: String(campaign.interval_minutes ?? DEFAULT_SETTINGS.interval_minutes),
    jitter_minutes: String(campaign.jitter_minutes ?? DEFAULT_SETTINGS.jitter_minutes),
    daily_start_time: campaign.daily_start_time
      || minuteToTime(campaign.daily_start_minute, DEFAULT_SETTINGS.daily_start_time),
    daily_end_time: campaign.daily_end_time
      || minuteToTime(campaign.daily_end_minute, DEFAULT_SETTINGS.daily_end_time),
    weekdays,
    max_posts_per_day: String(campaign.max_posts_per_day ?? DEFAULT_SETTINGS.max_posts_per_day),
    post_ttl_days: String(campaign.post_ttl_days ?? DEFAULT_SETTINGS.post_ttl_days),
    default_caption: campaign.default_caption || '',
    wall_ids: Array.isArray(campaign.wall_ids) ? campaign.wall_ids.join('\n') : '',
    fyp_enabled: campaign.fyp_enabled !== false,
  }
}

function payloadFromDraft(draft) {
  const wallIds = [...new Set(
    String(draft.wall_ids || '')
      .split(/[\n,]+/)
      .map((value) => value.trim())
      .filter(Boolean),
  )]
  return {
    enabled: Boolean(draft.enabled),
    timezone: String(draft.timezone || '').trim(),
    start_at: zonedDateTimeToIso(draft.start_at, draft.timezone),
    interval_minutes: Number(draft.interval_minutes),
    jitter_minutes: Number(draft.jitter_minutes),
    daily_start_time: draft.daily_start_time,
    daily_end_time: draft.daily_end_time,
    weekdays: [...draft.weekdays].sort((a, b) => a - b),
    max_posts_per_day: Number(draft.max_posts_per_day),
    post_ttl_days: Number(draft.post_ttl_days),
    default_caption: String(draft.default_caption || '').trim() || null,
    wall_ids: wallIds,
    fyp_enabled: Boolean(draft.fyp_enabled),
  }
}

function formatDate(value, timezone) {
  if (!value) return 'Not scheduled'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  try {
    return new Intl.DateTimeFormat('en', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timezone || 'UTC',
    }).format(date)
  } catch {
    return date.toLocaleString()
  }
}

function formatBytes(value) {
  const bytes = Number(value)
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatNumber(value) {
  return new Intl.NumberFormat('en').format(Number(value) || 0)
}

function formatPercent(value) {
  const number = Number(value)
  return Number.isFinite(number) ? `${number.toFixed(1)}%` : '—'
}

function analyticsData(value) {
  const data = unwrapData(value)
  return data && typeof data === 'object' ? data : null
}

function resultItem(value) {
  const data = unwrapData(value)
  return data.item ?? data
}

function statusTone(status) {
  if (status === 'published') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'publishing' || status === 'review') return 'warning'
  return 'neutral'
}

function isVideo(item) {
  return String(item?.media_type || '').startsWith('video')
    || /\.(mp4|mov|webm|m4v)$/i.test(item?.original_filename || '')
}

function isAudio(item) {
  return String(item?.media_type || '').startsWith('audio')
    || /\.(mp3|wav|m4a|ogg)$/i.test(item?.original_filename || '')
}

export default function FanslyFypManager({ modelId, variant = 'classic' }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [items, setItems] = useState([])
  const [provider, setProvider] = useState({ connected: false, status: 'not_connected' })
  const [queueTotal, setQueueTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [savingSettings, setSavingSettings] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [mediaUploading, setMediaUploading] = useState(false)
  const [selectedMediaFiles, setSelectedMediaFiles] = useState([])
  const [mediaCaption, setMediaCaption] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [mediaDragActive, setMediaDragActive] = useState(false)
  const [busyAction, setBusyAction] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState({ caption: '', scheduled_at: '' })
  const [message, setMessage] = useState({ tone: '', text: '' })
  const [preview, setPreview] = useState(null)
  const [analytics, setAnalytics] = useState(null)
  const [analyticsRange, setAnalyticsRange] = useState('last_24h')
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [analyticsError, setAnalyticsError] = useState('')
  const fileInputRef = useRef(null)
  const mediaInputRef = useRef(null)
  const loadRequestRef = useRef(0)
  const analyticsRequestRef = useRef(0)
  const previewRequestRef = useRef(0)
  const previewCloseRef = useRef(null)
  const previewTriggerRef = useRef(null)

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!modelId) return
    const requestId = loadRequestRef.current + 1
    loadRequestRef.current = requestId
    if (!quiet) setLoading(true)
    if (!quiet) setMessage({ tone: '', text: '' })
    try {
      const result = await getFanslyFyp(modelId)
      if (requestId !== loadRequestRef.current) return
      const data = unwrapData(result)
      setSettings(fromCampaign(result))
      setItems(itemList(result))
      setProvider(data.provider || { connected: false, status: 'not_connected' })
      setQueueTotal(Number(data.total) || itemList(result).length)
    } catch (error) {
      if (requestId !== loadRequestRef.current) return
      setMessage({ tone: 'error', text: getApiErrorMessage(error, 'Failed to load Fansly FYP') })
    }
    if (requestId === loadRequestRef.current) setLoading(false)
  }, [modelId])

  const loadAnalytics = useCallback(async ({ force = false } = {}) => {
    if (!modelId) return
    const requestId = analyticsRequestRef.current + 1
    analyticsRequestRef.current = requestId
    setAnalyticsLoading(true)
    setAnalyticsError('')
    try {
      const result = await getFanslyFypAnalytics(modelId, analyticsRange, force)
      if (requestId !== analyticsRequestRef.current) return
      setAnalytics(analyticsData(result))
    } catch (error) {
      if (requestId !== analyticsRequestRef.current) return
      setAnalyticsError(getApiErrorMessage(error, 'Failed to load Fansly FYP analytics'))
    } finally {
      if (requestId === analyticsRequestRef.current) setAnalyticsLoading(false)
    }
  }, [analyticsRange, modelId])

  useEffect(() => {
    setSettings(DEFAULT_SETTINGS)
    setItems([])
    setProvider({ connected: false, status: 'not_connected' })
    setQueueTotal(0)
    setSelectedFile(null)
    setSelectedMediaFiles([])
    setMediaCaption('')
    setEditingId(null)
    setAnalytics(null)
    setAnalyticsError('')
    load()
    return () => {
      loadRequestRef.current += 1
      analyticsRequestRef.current += 1
      previewRequestRef.current += 1
    }
  }, [load])

  useEffect(() => {
    loadAnalytics()
    return () => { analyticsRequestRef.current += 1 }
  }, [loadAnalytics])

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url)
  }, [preview?.url])

  useEffect(() => {
    if (!preview) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    previewCloseRef.current?.focus()
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closePreview()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [preview])

  const counts = useMemo(() => items.reduce((result, item) => {
    const key = item.status || 'queued'
    result[key] = (result[key] || 0) + 1
    return result
  }, {}), [items])

  const analyticsMaxViews = useMemo(() => Math.max(
    1,
    ...(analytics?.trend || []).map((point) => Number(point.views) || 0),
  ), [analytics])

  function updateSetting(key, value) {
    setSettings((current) => ({ ...current, [key]: value }))
  }

  function toggleWeekday(day) {
    setSettings((current) => {
      const exists = current.weekdays.includes(day)
      return {
        ...current,
        weekdays: exists
          ? current.weekdays.filter((value) => value !== day)
          : [...current.weekdays, day],
      }
    })
  }

  async function handleSaveSettings(event) {
    event.preventDefault()
    const payload = payloadFromDraft(settings)
    if (!payload.timezone) {
      setMessage({ tone: 'error', text: 'Timezone is required.' })
      return
    }
    if (!payload.weekdays.length) {
      setMessage({ tone: 'error', text: 'Select at least one posting day.' })
      return
    }
    if (payload.wall_ids.length > 20) {
      setMessage({ tone: 'error', text: 'Use no more than 20 wall IDs.' })
      return
    }
    if (payload.jitter_minutes >= payload.interval_minutes) {
      setMessage({ tone: 'error', text: 'Jitter must be shorter than the posting interval.' })
      return
    }
    if (payload.daily_end_time <= payload.daily_start_time) {
      setMessage({ tone: 'error', text: 'Daily end time must be after the start time.' })
      return
    }
    setSavingSettings(true)
    setMessage({ tone: '', text: '' })
    try {
      const updated = await updateFanslyFypSettings(modelId, payload)
      setSettings(fromCampaign(updated))
      setMessage({ tone: 'success', text: 'Fansly FYP settings saved.' })
    } catch (error) {
      setMessage({ tone: 'error', text: getApiErrorMessage(error, 'Failed to save Fansly FYP settings') })
    } finally {
      setSavingSettings(false)
    }
  }

  function selectArchive(file) {
    if (!file) return
    if (!file.name?.toLowerCase().endsWith('.zip')) {
      setSelectedFile(null)
      setMessage({ tone: 'error', text: 'Select a .zip archive.' })
      return
    }
    setSelectedFile(file)
    setMessage({ tone: '', text: '' })
  }

  function selectMediaFiles(fileList) {
    const files = Array.from(fileList || [])
    if (!files.length) return
    if (files.length > 50) {
      setSelectedMediaFiles([])
      setMessage({ tone: 'error', text: 'Select no more than 50 media files.' })
      return
    }
    const totalBytes = files.reduce((total, file) => total + Number(file.size || 0), 0)
    if (totalBytes > MAX_MEDIA_UPLOAD_BYTES) {
      setSelectedMediaFiles([])
      setMessage({ tone: 'error', text: 'Selected media exceeds the 240 MB upload limit.' })
      return
    }
    const unsupported = files.find((file) => !/\.(jpe?g|png|webp|gif|heic|heif|mp4|mov|m4v|webm)$/i.test(file.name || ''))
    if (unsupported) {
      setSelectedMediaFiles([])
      setMessage({ tone: 'error', text: `${unsupported.name || 'A file'} is not a supported image or video.` })
      return
    }
    setSelectedMediaFiles(files)
    setMessage({ tone: '', text: '' })
  }

  async function handleMediaUpload(event) {
    event.preventDefault()
    if (!selectedMediaFiles.length) {
      setMessage({ tone: 'error', text: 'Select at least one image or video.' })
      return
    }
    setMediaUploading(true)
    setMessage({ tone: '', text: '' })
    try {
      const result = await uploadFanslyFypMedia(modelId, selectedMediaFiles, mediaCaption)
      const data = unwrapData(result)
      const imported = Number(data.imported)
      const duplicates = Number(data.skipped_duplicate) || 0
      await load({ quiet: true })
      setMessage({
        tone: 'success',
        text: `${Number.isFinite(imported) ? imported : selectedMediaFiles.length} media file${imported === 1 ? '' : 's'} added${duplicates ? `; ${duplicates} duplicate${duplicates === 1 ? '' : 's'} skipped` : ''}.`,
      })
      setSelectedMediaFiles([])
      setMediaCaption('')
      if (mediaInputRef.current) mediaInputRef.current.value = ''
    } catch (error) {
      setMessage({ tone: 'error', text: getApiErrorMessage(error, 'Failed to add Fansly media') })
    } finally {
      setMediaUploading(false)
    }
  }

  async function handleUpload(event) {
    event.preventDefault()
    if (!selectedFile) {
      setMessage({ tone: 'error', text: 'Select a ZIP archive first.' })
      return
    }
    setUploading(true)
    setMessage({ tone: '', text: '' })
    try {
      const result = await uploadFanslyFypArchive(modelId, selectedFile)
      const data = unwrapData(result)
      const imported = data.imported_count ?? data.created_count ?? data.imported
      await load({ quiet: true })
      setMessage({
        tone: 'success',
        text: Number.isFinite(Number(imported))
          ? `${Number(imported)} queue item${Number(imported) === 1 ? '' : 's'} imported.`
          : 'ZIP archive imported.',
      })
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (error) {
      setMessage({ tone: 'error', text: getApiErrorMessage(error, 'Failed to import Fansly FYP archive') })
    } finally {
      setUploading(false)
    }
  }

  function startEdit(item) {
    setEditingId(item.id)
    setEditDraft({
      caption: item.caption || '',
      scheduled_at: toDateTimeInput(item.scheduled_at, settings.timezone),
    })
  }

  async function saveItem(event, item) {
    event.preventDefault()
    const key = `save:${item.id}`
    setBusyAction(key)
    setMessage({ tone: '', text: '' })
    try {
      const result = await updateFanslyFypItem(modelId, item.id, {
        caption: editDraft.caption.trim() || null,
        scheduled_at: zonedDateTimeToIso(editDraft.scheduled_at, settings.timezone),
      })
      const updated = resultItem(result)
      setItems((current) => current.map((row) => (
        row.id === item.id ? { ...row, ...updated } : row
      )))
      setEditingId(null)
      setMessage({ tone: 'success', text: 'Queue item updated.' })
    } catch (error) {
      setMessage({ tone: 'error', text: getApiErrorMessage(error, 'Failed to update queue item') })
    } finally {
      setBusyAction('')
    }
  }

  async function deleteItem(item) {
    if (!window.confirm(`Delete ${item.original_filename || `queue item #${item.id}`}?`)) return
    const key = `delete:${item.id}`
    setBusyAction(key)
    setMessage({ tone: '', text: '' })
    try {
      await deleteFanslyFypItem(modelId, item.id)
      setItems((current) => current.filter((row) => row.id !== item.id))
      setMessage({ tone: 'success', text: 'Queue item deleted.' })
    } catch (error) {
      setMessage({ tone: 'error', text: getApiErrorMessage(error, 'Failed to delete queue item') })
    } finally {
      setBusyAction('')
    }
  }

  async function runItemAction(kind, item) {
    const confirmReviewRetry = item.status === 'review'
    if (confirmReviewRetry && !window.confirm(
      'Fansly may already contain this post. Check the Fansly profile first. Retry only if the post is not there.',
    )) return
    const key = `${kind}:${item.id}`
    setBusyAction(key)
    setMessage({ tone: '', text: '' })
    try {
      const result = kind === 'retry'
        ? await retryFanslyFypItem(modelId, item.id, { confirmReviewRetry })
        : await publishFanslyFypItemNow(modelId, item.id, { confirmReviewRetry })
      const updated = resultItem(result)
      if (updated?.id) {
        setItems((current) => current.map((row) => (
          row.id === item.id ? { ...row, ...updated } : row
        )))
      } else {
        await load({ quiet: true })
      }
      setMessage({
        tone: 'success',
        text: kind === 'retry' ? 'Retry queued.' : 'Publish started.',
      })
    } catch (error) {
      setMessage({
        tone: 'error',
        text: getApiErrorMessage(error, kind === 'retry' ? 'Failed to retry item' : 'Failed to publish item'),
      })
    } finally {
      setBusyAction('')
    }
  }

  async function openPreview(item) {
    const requestId = previewRequestRef.current + 1
    previewRequestRef.current = requestId
    previewTriggerRef.current = document.activeElement
    setPreview({ item, loading: true, url: '', error: '' })
    try {
      const url = await fetchFanslyFypPreview(modelId, item.id)
      if (requestId !== previewRequestRef.current) {
        URL.revokeObjectURL(url)
        return
      }
      setPreview((current) => current?.item?.id === item.id
        ? { item, loading: false, url, error: '' }
        : current)
    } catch (error) {
      if (requestId !== previewRequestRef.current) return
      setPreview((current) => current?.item?.id === item.id
        ? { item, loading: false, url: '', error: getApiErrorMessage(error, 'Preview unavailable') }
        : current)
    }
  }

  function closePreview() {
    previewRequestRef.current += 1
    setPreview(null)
    window.requestAnimationFrame(() => previewTriggerRef.current?.focus?.())
  }

  const sectionId = `fansly-fyp-${modelId}`
  const archiveId = `${sectionId}-archive`
  const mediaId = `${sectionId}-media`

  return (
    <section className={`fansly-fyp fansly-fyp--${variant}`} aria-labelledby={`${sectionId}-title`}>
      <header className="fansly-fyp__header">
        <div>
          <p className="fansly-fyp__eyebrow">Fansly publishing</p>
          <h2 id={`${sectionId}-title`}>Fansly FYP</h2>
        </div>
        <div className="fansly-fyp__summary" aria-label="Queue status">
          <span className={`fansly-fyp__state ${settings.enabled ? 'is-enabled' : ''}`}>
            {settings.enabled ? 'Schedule on' : 'Schedule off'}
          </span>
          <span>{queueTotal} total</span>
          <span className={provider.connected ? 'is-connected' : 'is-danger'}>
            {provider.connected ? 'Fansly connected' : 'Fansly not connected'}
          </span>
          {provider.connected ? (
            <span>{provider.transport === 'direct' ? 'Direct API' : 'OnlyFansAPI'}</span>
          ) : null}
          {counts.failed ? <span className="is-danger">{counts.failed} failed</span> : null}
          <button type="button" className="fansly-fyp__button is-quiet" onClick={() => load()} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="fansly-fyp__live" aria-live="polite" aria-atomic="true">
        {message.text ? (
          <div className={`fansly-fyp__notice is-${message.tone}`} role={message.tone === 'error' ? 'alert' : 'status'}>
            {message.text}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="fansly-fyp__loading" role="status">Loading Fansly FYP...</div>
      ) : (
        <>
          <section className="fansly-fyp__analytics" aria-labelledby={`${sectionId}-analytics-title`}>
            <div className="fansly-fyp__subhead">
              <div>
                <h3 id={`${sectionId}-analytics-title`}>Post analytics</h3>
                <span>
                  {analytics?.fetched_at ? `Updated ${formatDate(analytics.fetched_at, settings.timezone)}` : 'Fansly Insights'}
                  {analytics?.cached ? ' · cached' : ''}
                </span>
              </div>
              <div className="fansly-fyp__analytics-actions">
                <select
                  value={analyticsRange}
                  onChange={(event) => setAnalyticsRange(event.target.value)}
                  aria-label="Analytics range"
                >
                  <option value="last_24h">Last 24 hours</option>
                  <option value="last_7_days">Last 7 days</option>
                </select>
                <button
                  type="button"
                  className="fansly-fyp__button is-quiet"
                  disabled={analyticsLoading}
                  title="Fetch fresh Fansly data; uses 1 API credit"
                  onClick={() => loadAnalytics({ force: true })}
                >
                  {analyticsLoading ? 'Loading...' : 'Refresh data'}
                </button>
              </div>
            </div>

            {analyticsError ? <div className="fansly-fyp__notice is-error" role="alert">{analyticsError}</div> : null}
            {analyticsLoading && !analytics ? <div className="fansly-fyp__analytics-loading">Loading analytics...</div> : null}
            {analytics ? (
              <>
                <div className="fansly-fyp__metric-grid">
                  <div><span>FYP views</span><strong>{formatNumber(analytics.totals?.views)}</strong></div>
                  <div><span>Unique viewers</span><strong>{formatNumber(analytics.totals?.unique_viewers)}</strong></div>
                  <div><span>Average watch</span><strong>{Number(analytics.totals?.avg_watch_seconds || 0).toFixed(2)}s</strong></div>
                  <div><span>Average viewed</span><strong>{formatPercent(analytics.totals?.avg_watched_percent)}</strong></div>
                  <div>
                    <span>Recent pace</span>
                    <strong className={Number(analytics.recent_half?.views_change_percent) >= 0 ? 'is-positive' : 'is-negative'}>
                      {analytics.recent_half?.views_change_percent == null
                        ? '—'
                        : `${Number(analytics.recent_half.views_change_percent) >= 0 ? '+' : ''}${formatPercent(analytics.recent_half.views_change_percent)}`}
                    </strong>
                  </div>
                  <div><span>Publish success</span><strong>{formatPercent(analytics.publishing?.success_percent)}</strong></div>
                </div>

                <div className="fansly-fyp__trend" aria-label="FYP views trend">
                  {(analytics.trend || []).map((point) => (
                    <span
                      key={point.timestamp}
                      style={{ height: `${Math.max(4, (Number(point.views) || 0) / analyticsMaxViews * 100)}%` }}
                      title={`${formatDate(point.timestamp, settings.timezone)}: ${formatNumber(point.views)} views`}
                    />
                  ))}
                </div>

                <div className="fansly-fyp__analytics-table-wrap">
                  <table className="fansly-fyp__analytics-table">
                    <thead><tr><th>Top media</th><th>Views</th><th>Avg watch</th><th>Likes</th><th>Source</th><th /></tr></thead>
                    <tbody>
                      {(analytics.top_media || []).map((media) => (
                        <tr key={media.media_id}>
                          <td><strong>{media.filename}</strong>{media.duration_seconds ? <small>{media.duration_seconds}s</small> : null}</td>
                          <td>{formatNumber(media.views)}</td>
                          <td>{Number(media.avg_watch_seconds || 0).toFixed(2)}s</td>
                          <td>{formatNumber(media.likes)}</td>
                          <td>{media.source === 'fanltv' ? 'FanLTV' : 'External'}</td>
                          <td>{media.item_id ? (
                            <button
                              type="button"
                              className="fansly-fyp__button is-quiet"
                              onClick={() => {
                                const item = items.find((row) => row.id === media.item_id)
                                if (item) openPreview(item)
                              }}
                            >
                              Preview
                            </button>
                          ) : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="fansly-fyp__analytics-note">
                  Cached for 6 hours. Manual refresh uses 1 OnlyFansAPI credit and is limited to once per 15 minutes.
                  {analytics.refresh_limited ? ' The latest refresh used cached data.' : ''}
                </p>
              </>
            ) : null}
          </section>

          <div className="fansly-fyp__controls">
            <form className="fansly-fyp__settings" onSubmit={handleSaveSettings}>
              <div className="fansly-fyp__subhead">
                <div>
                  <h3>Schedule settings</h3>
                  <span>{settings.timezone || 'UTC'}</span>
                </div>
                <label className="fansly-fyp__switch">
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    disabled={!provider.connected && !settings.enabled}
                    onChange={(event) => updateSetting('enabled', event.target.checked)}
                  />
                  <span>Enabled</span>
                </label>
              </div>

              <div className="fansly-fyp__form-grid">
                <label className="fansly-fyp__field">
                  <span>Timezone</span>
                  <input
                    type="text"
                    value={settings.timezone}
                    onChange={(event) => updateSetting('timezone', event.target.value)}
                    placeholder="UTC"
                    autoComplete="off"
                    required
                  />
                </label>
                <label className="fansly-fyp__field">
                  <span>Start at</span>
                  <input
                    type="datetime-local"
                    value={settings.start_at}
                    onChange={(event) => updateSetting('start_at', event.target.value)}
                  />
                </label>
                <label className="fansly-fyp__field">
                  <span>Interval, minutes</span>
                  <input
                    type="number"
                    min="15"
                    max="10080"
                    step="1"
                    value={settings.interval_minutes}
                    onChange={(event) => updateSetting('interval_minutes', event.target.value)}
                    required
                  />
                </label>
                <label className="fansly-fyp__field">
                  <span>Jitter, minutes</span>
                  <input
                    type="number"
                    min="0"
                    max="720"
                    step="1"
                    value={settings.jitter_minutes}
                    onChange={(event) => updateSetting('jitter_minutes', event.target.value)}
                    required
                  />
                </label>
                <label className="fansly-fyp__field">
                  <span>Daily start</span>
                  <input
                    type="time"
                    value={settings.daily_start_time}
                    onChange={(event) => updateSetting('daily_start_time', event.target.value)}
                    required
                  />
                </label>
                <label className="fansly-fyp__field">
                  <span>Daily end</span>
                  <input
                    type="time"
                    value={settings.daily_end_time}
                    onChange={(event) => updateSetting('daily_end_time', event.target.value)}
                    required
                  />
                </label>
                <label className="fansly-fyp__field">
                  <span>Max posts per day</span>
                  <input
                    type="number"
                    min="1"
                    max="240"
                    step="1"
                    value={settings.max_posts_per_day}
                    onChange={(event) => updateSetting('max_posts_per_day', event.target.value)}
                    required
                  />
                </label>
                <label className="fansly-fyp__field">
                  <span>Post lifetime, days</span>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    step="1"
                    value={settings.post_ttl_days}
                    onChange={(event) => updateSetting('post_ttl_days', event.target.value)}
                    required
                  />
                </label>
                <label className="fansly-fyp__switch fansly-fyp__switch--field">
                  <input
                    type="checkbox"
                    checked={settings.fyp_enabled}
                    onChange={(event) => updateSetting('fyp_enabled', event.target.checked)}
                  />
                  <span>Fansly FYP enabled</span>
                </label>
              </div>

              <fieldset className="fansly-fyp__weekdays">
                <legend>Posting days</legend>
                <div>
                  {WEEKDAYS.map((day) => (
                    <label key={day.value} className={settings.weekdays.includes(day.value) ? 'is-selected' : ''}>
                      <input
                        type="checkbox"
                        checked={settings.weekdays.includes(day.value)}
                        onChange={() => toggleWeekday(day.value)}
                      />
                      <span>{day.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="fansly-fyp__field fansly-fyp__field--wide">
                <span>Default caption</span>
                <textarea
                  rows="4"
                  maxLength="5000"
                  value={settings.default_caption}
                  onChange={(event) => updateSetting('default_caption', event.target.value)}
                />
              </label>
              <label className="fansly-fyp__field fansly-fyp__field--wide">
                <span>Wall IDs</span>
                <textarea
                  rows="3"
                  value={settings.wall_ids}
                  onChange={(event) => updateSetting('wall_ids', event.target.value)}
                  placeholder="One wall ID per line"
                />
              </label>

              <div className="fansly-fyp__form-actions">
                <button type="submit" className="fansly-fyp__button is-primary" disabled={savingSettings}>
                  {savingSettings ? 'Saving...' : 'Save settings'}
                </button>
              </div>
            </form>

            <div className="fansly-fyp__uploads">
              <form className="fansly-fyp__media-upload" onSubmit={handleMediaUpload}>
                <div className="fansly-fyp__subhead">
                  <div>
                    <h3>Add media</h3>
                    <span>Images and videos</span>
                  </div>
                </div>
                <div
                  className={`fansly-fyp__dropzone is-compact ${mediaDragActive ? 'is-dragging' : ''}`}
                  onDragEnter={(event) => { event.preventDefault(); setMediaDragActive(true) }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={(event) => { event.preventDefault(); setMediaDragActive(false) }}
                  onDrop={(event) => {
                    event.preventDefault()
                    setMediaDragActive(false)
                    selectMediaFiles(event.dataTransfer.files)
                  }}
                >
                  <input
                    ref={mediaInputRef}
                    id={mediaId}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/quicktime,video/webm,.m4v,.heic,.heif"
                    onChange={(event) => selectMediaFiles(event.target.files)}
                  />
                  <span className="fansly-fyp__archive-mark" aria-hidden="true">MEDIA</span>
                  <strong>{selectedMediaFiles.length ? `${selectedMediaFiles.length} file${selectedMediaFiles.length === 1 ? '' : 's'} selected` : 'No media selected'}</strong>
                  {selectedMediaFiles.length ? <small>{formatBytes(selectedMediaFiles.reduce((total, file) => total + file.size, 0))}</small> : null}
                  <label htmlFor={mediaId} className="fansly-fyp__button is-quiet">Choose media</label>
                </div>
                <label className="fansly-fyp__field fansly-fyp__field--wide">
                  <span>Caption for selected files</span>
                  <textarea rows="3" maxLength="5000" value={mediaCaption} onChange={(event) => setMediaCaption(event.target.value)} />
                </label>
                <button type="submit" className="fansly-fyp__button is-primary" disabled={!selectedMediaFiles.length || mediaUploading}>
                  {mediaUploading ? 'Adding...' : 'Add to queue'}
                </button>
              </form>

              <form className="fansly-fyp__archive" onSubmit={handleUpload}>
                <div className="fansly-fyp__subhead">
                  <div>
                    <h3>ZIP archive</h3>
                    <span>Batch import</span>
                  </div>
                </div>
                <div
                  className={`fansly-fyp__dropzone is-compact ${dragActive ? 'is-dragging' : ''}`}
                  onDragEnter={(event) => { event.preventDefault(); setDragActive(true) }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={(event) => { event.preventDefault(); setDragActive(false) }}
                  onDrop={(event) => {
                    event.preventDefault()
                    setDragActive(false)
                    selectArchive(event.dataTransfer.files?.[0])
                  }}
                >
                  <input
                    ref={fileInputRef}
                    id={archiveId}
                    type="file"
                    accept=".zip,application/zip"
                    onChange={(event) => selectArchive(event.target.files?.[0])}
                  />
                  <span className="fansly-fyp__archive-mark" aria-hidden="true">ZIP</span>
                  <strong>{selectedFile?.name || 'No archive selected'}</strong>
                  {selectedFile ? <small>{formatBytes(selectedFile.size)}</small> : null}
                  <label htmlFor={archiveId} className="fansly-fyp__button is-quiet">Choose ZIP</label>
                </div>
                <button type="submit" className="fansly-fyp__button is-primary" disabled={!selectedFile || uploading}>
                  {uploading ? 'Importing...' : 'Import archive'}
                </button>
              </form>
            </div>
          </div>

          <section className="fansly-fyp__queue" aria-labelledby={`${sectionId}-queue-title`}>
            <div className="fansly-fyp__subhead">
              <div>
                <h3 id={`${sectionId}-queue-title`}>Publishing queue</h3>
                <span>{counts.queued || 0} queued / {counts.published || 0} published / {counts.review || 0} review</span>
              </div>
            </div>

            {!items.length ? (
              <div className="fansly-fyp__empty">Queue is empty.</div>
            ) : (
              <ul className="fansly-fyp__queue-list">
                {items.map((item) => {
                  const status = item.status || 'queued'
                  const actionBusy = busyAction.endsWith(`:${item.id}`)
                  const canPublish = !['uploading', 'publishing', 'published'].includes(status)
                  const canModify = !['uploading', 'publishing', 'published'].includes(status)
                  return (
                    <li key={item.id} className="fansly-fyp__queue-item">
                      <div className="fansly-fyp__item-main">
                        <div className="fansly-fyp__file">
                          <button
                            type="button"
                            className="fansly-fyp__preview-button"
                            onClick={() => openPreview(item)}
                            aria-label={`Preview ${item.original_filename || `item ${item.id}`}`}
                          >
                            <span aria-hidden="true">{isVideo(item) ? 'VID' : isAudio(item) ? 'AUD' : 'IMG'}</span>
                          </button>
                          <div>
                            <strong>{item.original_filename || `Queue item #${item.id}`}</strong>
                            <span>{[item.media_type, formatBytes(item.size_bytes)].filter(Boolean).join(' / ')}</span>
                          </div>
                        </div>
                        <div className="fansly-fyp__item-status">
                          <span className={`fansly-fyp__status is-${statusTone(status)}`}>
                            {STATUS_LABELS[status] || status}
                          </span>
                          {item.attempts ? <small>{item.attempts} attempt{item.attempts === 1 ? '' : 's'}</small> : null}
                        </div>
                        <div className="fansly-fyp__item-time">
                          <span>Scheduled</span>
                          <strong>{formatDate(item.scheduled_at, settings.timezone)}</strong>
                        </div>
                        <div className="fansly-fyp__item-actions">
                          <button type="button" className="fansly-fyp__button is-quiet" onClick={() => openPreview(item)}>
                            Preview
                          </button>
                          <button type="button" className="fansly-fyp__button is-quiet" disabled={!canModify || actionBusy} onClick={() => startEdit(item)}>
                            Edit
                          </button>
                          {status === 'failed' ? (
                            <button
                              type="button"
                              className="fansly-fyp__button is-warning"
                              disabled={actionBusy}
                              onClick={() => runItemAction('retry', item)}
                            >
                              Retry
                            </button>
                          ) : null}
                          {status === 'review' ? (
                            <button
                              type="button"
                              className="fansly-fyp__button is-warning"
                              disabled={actionBusy}
                              onClick={() => runItemAction('retry', item)}
                            >
                              Confirm retry
                            </button>
                          ) : null}
                          {canPublish && status !== 'review' ? (
                            <button
                              type="button"
                              className="fansly-fyp__button is-primary"
                              disabled={actionBusy}
                              onClick={() => runItemAction('publish', item)}
                            >
                              Publish now
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="fansly-fyp__button is-danger"
                            disabled={!canModify || actionBusy}
                            onClick={() => deleteItem(item)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {item.caption ? <p className="fansly-fyp__caption">{item.caption}</p> : null}
                      {item.error ? <p className="fansly-fyp__item-error" role="alert">{item.error}</p> : null}

                      {editingId === item.id ? (
                        <form className="fansly-fyp__item-edit" onSubmit={(event) => saveItem(event, item)}>
                          <label className="fansly-fyp__field">
                            <span>Caption</span>
                            <textarea
                              rows="3"
                              maxLength="5000"
                              value={editDraft.caption}
                              onChange={(event) => setEditDraft((current) => ({ ...current, caption: event.target.value }))}
                              autoFocus
                            />
                          </label>
                          <label className="fansly-fyp__field">
                            <span>Scheduled at</span>
                            <input
                              type="datetime-local"
                              value={editDraft.scheduled_at}
                              onChange={(event) => setEditDraft((current) => ({ ...current, scheduled_at: event.target.value }))}
                            />
                          </label>
                          <div className="fansly-fyp__form-actions">
                            <button type="submit" className="fansly-fyp__button is-primary" disabled={busyAction === `save:${item.id}`}>
                              {busyAction === `save:${item.id}` ? 'Saving...' : 'Save item'}
                            </button>
                            <button type="button" className="fansly-fyp__button is-quiet" onClick={() => setEditingId(null)}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </>
      )}

      {preview ? (
        <div className="fansly-fyp__preview-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closePreview()
        }}>
          <div className="fansly-fyp__preview-dialog" role="dialog" aria-modal="true" aria-labelledby={`${sectionId}-preview-title`}>
            <header>
              <h3 id={`${sectionId}-preview-title`}>{preview.item.original_filename || 'Media preview'}</h3>
              <button ref={previewCloseRef} type="button" className="fansly-fyp__close" onClick={closePreview} aria-label="Close preview">x</button>
            </header>
            <div className="fansly-fyp__preview-content">
              {preview.loading ? <div className="fansly-fyp__loading" role="status">Loading preview...</div> : null}
              {preview.error ? <div className="fansly-fyp__notice is-error" role="alert">{preview.error}</div> : null}
              {preview.url && isVideo(preview.item) ? <video src={preview.url} controls autoPlay /> : null}
              {preview.url && isAudio(preview.item) ? <audio src={preview.url} controls autoPlay /> : null}
              {preview.url && !isVideo(preview.item) && !isAudio(preview.item) ? (
                <img src={preview.url} alt={preview.item.original_filename || 'Fansly FYP media preview'} />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
